/**
 * ADR-0221 — client for the authenticated per-site readiness snapshot.
 *
 * Two calls:
 *   - `getSiteReadinessSnapshot(http, site_id)` → `GET /v1/sites/:site_id/readiness`
 *   - `triggerSiteReadinessRun(http, site_id)`  → `POST /v1/sites/:site_id/readiness/run`
 *
 * Both return discriminated-union results so the caller surfaces
 * `rate_limited` + `retry_after_seconds` without catching exceptions.
 */

import { ApiError, type HttpClient } from './http-client.js';

/** Mirrors the server-side `StoredSignals` shape — closed enums + numbers
 *  only. Every field optional (missing = never got a definite value). */
export interface SiteReadinessStoredSignals {
  readonly sill_installed?: 'present' | 'absent' | 'unknown';
  readonly agent_card?: 'present' | 'absent' | 'unknown';
  readonly llms_txt?: 'present' | 'absent' | 'unknown';
  readonly robots_txt?: 'present' | 'absent' | 'unknown';
  readonly sitemap?: 'present' | 'absent' | 'unknown';
  readonly ai_access?: 'open' | 'partial' | 'blocked' | 'unknown';
  readonly structured_data?: 'present' | 'absent' | 'unknown';
  readonly mcp?: 'present' | 'absent' | 'unknown';
  readonly api_catalog?: 'present' | 'absent' | 'unknown';
  /** ADR-0223 — ARD well-known catalog. */
  readonly ard?: 'present' | 'absent' | 'unknown';
  /** ADR-0223 — UCP business profile. */
  readonly ucp?: 'present' | 'absent' | 'unknown';
  /** ADR-0225 — informational "charges agents (x402)" tri-state. */
  readonly x402?: 'present' | 'absent' | 'unknown';
  /** ADR-0225 — closed sub-kind when `x402 === 'present'`. */
  readonly x402_kind?: 'x402' | 'pay_per_crawl';
  /** ADR-0252 — informational PAP discovery file tri-state. */
  readonly pap?: 'present' | 'absent' | 'unknown';
  readonly content_signals?: 'present' | 'absent' | 'unknown';
  readonly crawler_access?: 'open' | 'blocked' | 'unknown';
  readonly agent_gateway?: 'present' | 'absent' | 'unknown';
  readonly platform?: 'shopify' | 'woocommerce' | 'wordpress' | 'other_known' | 'unknown';
  readonly platform_confidence?: 'high' | 'medium' | 'low' | null;
  readonly llms_txt_quality?: 'ok' | 'thin' | 'unknown';
  readonly structured_data_class?: string;
  readonly mcp_liveness?: string;
  readonly agent_card_auth_required?: boolean;
  readonly agent_card_tools_count?: number;
  readonly agent_card_protocols?: readonly string[];
  readonly ai_bots_status?: Readonly<Record<string, 'allowed' | 'blocked' | 'not_mentioned'>>;
}

export interface SiteReadinessSnapshot {
  readonly site_id: string;
  readonly account_id: string;
  readonly signals: SiteReadinessStoredSignals;
  readonly signal_checked_at: Readonly<Record<string, string>>;
  readonly outcome: 'checked' | 'unreachable' | 'unchecked' | null;
  readonly status: 'running' | 'completed' | 'partial' | 'failed';
  readonly started_at: string | null;
  readonly completed_at: string | null;
  readonly trigger: 'manual' | 'schedule' | 'page_load' | null;
  readonly in_progress: boolean;
}

interface GetSnapshotEnvelope {
  snapshot: SiteReadinessSnapshot;
}

export type GetSnapshotResult =
  | { readonly kind: 'ok'; readonly snapshot: SiteReadinessSnapshot }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error' };

export async function getSiteReadinessSnapshot(
  http: HttpClient,
  site_id: string,
): Promise<GetSnapshotResult> {
  try {
    const res = await http.request<GetSnapshotEnvelope>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/readiness`,
    });
    if (res === undefined) return { kind: 'error' };
    // Defensive: a malformed body (test stub returning `{ items: [] }`,
    // or a server-side regression) can set `res.snapshot === undefined`.
    // Treating that as `ok` propagates `undefined` through the hook and
    // crashes any downstream `snapshot.signals` read. Treat as an
    // error so the UI falls back to its empty-state path.
    if (res.snapshot === undefined || res.snapshot === null) return { kind: 'error' };
    return { kind: 'ok', snapshot: res.snapshot };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return { kind: 'not_found' };
    return { kind: 'error' };
  }
}

export type TriggerRunResult =
  | { readonly kind: 'started'; readonly started_at: string }
  | {
      readonly kind: 'rate_limited';
      readonly retry_after_seconds: number;
      /** ADR-0221 — which cap tripped. 'site' is the usual one; 'target'
       *  tells support that another account aimed enough runs at the
       *  same domain. */
      readonly dimension: 'site' | 'account' | 'target';
    }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error' };

interface TriggerEnvelope {
  status: 'running';
  started_at: string;
}

export async function triggerSiteReadinessRun(
  http: HttpClient,
  site_id: string,
): Promise<TriggerRunResult> {
  try {
    const res = await http.request<TriggerEnvelope>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/readiness/run`,
    });
    if (res === undefined || res.started_at === undefined) return { kind: 'error' };
    return { kind: 'started', started_at: res.started_at };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'rate_limited') {
        const retry = err.retry_after_seconds ?? 3600;
        // `dimension` is on the body (not an ApiError field); default to
        // 'site' when the server didn't carry one.
        return { kind: 'rate_limited', retry_after_seconds: retry, dimension: 'site' };
      }
    }
    return { kind: 'error' };
  }
}
